import { Blueprint } from '../../blueprint.class';
import { Size } from '../../../../types';
import { ToolResult } from '../../types';

export interface ResizeIslandArgs {
	islandId: string;
	width?: number;
	height?: number;
	depth?: number;
	overrideMinimum?: boolean;
}

export const resizeIslandTool = (blueprint: Blueprint, { islandId, width, height, depth, overrideMinimum }: ResizeIslandArgs): ToolResult => {
	if (!islandId) return { success: false, error: 'Missing islandId. Use list_islands to get valid island IDs.' };

	const kitchen = blueprint.kitchens.getActiveVersion();
	if (!kitchen) return { success: false, error: 'No active kitchen found.' };

	const island = kitchen.design.islands.children.find((i) => i.id === islandId);
	if (!island) return { success: false, error: `Island '${islandId}' not found. Use list_islands to get valid island IDs.` };

	// Get current size values
	const currentSize = island.size as Size;
	const currentWidth = currentSize.width;
	const currentHeight = currentSize.height;
	const currentDepth = currentSize.depth as number;

	// Use existing values when parameters are not passed or are 0
	const newWidth = width || currentWidth;
	const newHeight = height || currentHeight;
	const newDepth = depth || currentDepth;

	// Calculate minimum width based on appliances.
	//
	// FRONT PANEL ONLY — the island's width is driven by what is laid out
	// horizontally across its face. Summing every panel charged side- and
	// back-panel ventilation and passthrough against that budget, inflating
	// the minimum far past anything real (a 34" pizza island computed a 76"
	// minimum). Matches cleanKitchenTool, which already sizes from
	// `panels.getFrontPanel()`, and addApplianceTool's fit check.
	//
	// `panel.minimumWidth` owns the arithmetic (occupied + 2" edges + 1" per
	// gap + connected-island overlap) so this floor and addApplianceTool's fit
	// gate can no longer drift apart. Overlap matters here too: shrinking an
	// L-shape's island to a width that ignores the neighbour it meets leaves
	// the appliances no run to sit in.
	const frontPanel = island.panels.getFrontPanel();
	const minimumWidth = Math.max(frontPanel?.minimumWidth ?? 0, 36);

	let finalWidth = newWidth;
	let finalHeight = newHeight;
	let finalDepth = newDepth;
	let wasConstrained = false;
	const constraints: Array<string> = [];

	// Check if requested width is too small (appliance-based minimum)
	if (newWidth < minimumWidth) {
		finalWidth = minimumWidth;
		wasConstrained = true;
		constraints.push(`width clamped to ${minimumWidth}" (appliance minimum)`);
	}

	// Enforce island minimum size metadata (unless overrideMinimum is set)
	const minimumSize = island.minimumSize;
	if (minimumSize && !overrideMinimum) {
		if (finalWidth < minimumSize.width) {
			finalWidth = minimumSize.width;
			wasConstrained = true;
			constraints.push(`width clamped to ${minimumSize.width}" (island minimum)`);
		}
		if (finalHeight < minimumSize.height) {
			finalHeight = minimumSize.height;
			wasConstrained = true;
			constraints.push(`height clamped to ${minimumSize.height}" (island minimum)`);
		}
		if (finalDepth < (minimumSize.depth as number)) {
			finalDepth = minimumSize.depth as number;
			wasConstrained = true;
			constraints.push(`depth clamped to ${minimumSize.depth}" (island minimum)`);
		}
	}

	// Apply the new size
	console.log('[resize_island] Setting island.size', { islandId, finalWidth, finalHeight, finalDepth, initialized: island.initialized });
	island.size = { width: finalWidth, height: finalHeight, depth: finalDepth };
	console.log('[resize_island] island.size set complete');

	// If overrideMinimum, reset the minimum size to the new size
	if (overrideMinimum) {
		island.minimumSize = { width: finalWidth, height: finalHeight, depth: finalDepth };
	}

	if (wasConstrained) {
		return {
			success: true,
			message: `Island '${islandId}' was constrained: ${constraints.join(', ')}. ` +
				`Resized to ${finalWidth}x${finalHeight}x${finalDepth} inches.`
		};
	}

	return { success: true, message: `Island '${islandId}' resized to ${finalWidth}x${finalHeight}x${finalDepth} inches.` };
};
